import {
  Component,
  OnInit,
  EventEmitter,
  Output,
  ViewChild,
  SimpleChanges,
  Input,
  OnDestroy,
  OnChanges,
  ChangeDetectorRef,
  AfterViewInit,
} from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from 'src/app/services/api/api.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { ApprovalService } from 'src/app/services/approval/approval.service';
import { ToastService } from 'src/app/services/toast/toast.service';

type FileData = {
  label: string;
  files: Partial<AttachmentListValues>[];
};

type AttachmentArrProps = {
  labelArr: string[];
  referent: string;
};

export type ModalFilesUrlsProps = {
  url: string;
  isImg: boolean;
  name: string;
};
@Component({
  selector: 'app-dashboard-approval-attachments',
  templateUrl: './dashboard-approval-attachments.component.html',
  styleUrls: ['./dashboard-approval-attachments.component.scss'],
})
export class DashboardApprovalAttachmentsComponent
  implements OnInit, OnDestroy, OnChanges, AfterViewInit
{
  @ViewChild('modalRoot') modalRoot;
  @Input() comparingVersions: boolean = false;

  @Output() onClick = new EventEmitter<MouseEvent>();

  reproveForm = this.formBuilder.group({
    description: [null, [Validators.required]],
  });

  // Dados para versão principal
  mainData: FileData[] = [];
  mainApproval: any = null;
  mainRepresentatives: any[] = [];
  mainLoading = true;

  // Dados para versão comparando
  comparingData: FileData[] = [];
  comparingApproval: any = null;
  comparingRepresentatives: any[] = [];
  comparingLoading = true;
  comparingDataLoading = false;

  // Dados normais (sem comparação)
  data: FileData[] = [];

  warningModal = false;
  reproveModal = false;

  modalFiles: any = null;
  modalFilesUrls: ModalFilesUrlsProps[] | null = null;
  selectedFile: ModalFilesUrlsProps | null = null;
  selectedAttachment: Partial<AttachmentListValues> | null = null;

  representatives: any[] = [];
  approval;
  loading = true;

  eventMainVersion: any;
  eventComparingVersion: any;
  eventVersion: any;

  file_situations = ['Arquivo Enviado', 'Reprovado', 'Aprovado'];
  fileType = [
    {
      label: '.XML',
      value: 'xml',
    },
    {
      label: '.PDF',
      value: 'pdf',
    },
    {
      label: '.PNG',
      value: 'png',
    },
    {
      label: '.JPG',
      value: 'jpg',
    },
    {
      label: '.XLS',
      value: 'xls',
    },
    {
      label: '.XLSX',
      value: 'xlsx',
    },
    {
      label: '.DOC',
      value: 'doc',
    },
    {
      label: '.DOCX',
      value: 'docx',
    },
  ];

  canView = false;
  role: string = '';

  constructor(
    private router: Router,
    private formBuilder: FormBuilder,
    private toastService: ToastService,
    private api: ApiService,
    private sanitizer: DomSanitizer,
    private route: ActivatedRoute,
    private authService: AuthService,
    public approvalService: ApprovalService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    // Detectar quando comparingVersions muda de true para false
    if (
      changes['comparingVersions'] &&
      !changes['comparingVersions'].firstChange &&
      changes['comparingVersions'].previousValue === true &&
      changes['comparingVersions'].currentValue === false
    ) {
      // Recarregar dados para visualização normal
      if (this.approvalService.approvalData.attachmentsData) {
        this.approval = this.approvalService.approvalData.attachmentsData;
        this.representatives =
          this.approvalService.approvalData.attachmentsRepresentatives || [];
        this.filterData();
        // Garantir que tudo esteja processado antes de desativar loading
        this.cdr.detectChanges();
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.loading = false;
            this.cdr.detectChanges();
          });
        });
      } else {
        // Não chamar APIs v2 - sem dados no cache, não fazer loading
        this.approval = null;
        this.representatives = [];
        this.loading = false;
        this.cdr.detectChanges();
      }
    } else {
      // Não chamar APIs v2 - sem dados no cache, não fazer loading
      this.approval = null;
      this.representatives = [];
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  ngOnInit(): void {
    let range = this.router.url.split('/').length;
    this.role = this.router.url.split('/')[range - 2];

    // Botão de visualizar deve aparecer para todos
    this.canView = true;

    if (this.comparingVersions) {
      this.loadComparingVersions();
    } else {
      // Primeiro, tentar usar dados já carregados do cache
      if (this.approvalService.approvalData.attachmentsData) {
        this.approval = this.approvalService.approvalData.attachmentsData;
        this.representatives =
          this.approvalService.approvalData.attachmentsRepresentatives || [];

        // Garantir que os dados estejam totalmente processados antes de desativar loading
        this.filterData();

        // Garantir que os dados estejam realmente prontos antes de desativar loading
        // Verificar se os dados foram processados corretamente
        if (
          this.approval &&
          this.approval.register &&
          this.approval.register.files
        ) {
          // Aguardar um ciclo de detecção de mudanças para garantir que tudo foi processado
          this.cdr.detectChanges();
          // Usar requestAnimationFrame para garantir que a view foi atualizada
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              this.loading = false;
              this.cdr.detectChanges();
            });
          });
        } else {
          // Se os dados não estiverem completos, manter loading e fazer chamada
          this.getApprovalData();
        }
      } else {
        // Se não houver dados no cache, fazer chamada de API para files
        // MAS usar os dados de representative já carregados (não fazer nova chamada)
        // O loading será desativado dentro de getApprovalData() após tudo estar pronto
        this.getApprovalData();
      }
    }

    this.eventMainVersion = this.approvalService.changeMainVersion.subscribe(
      () => {
        if (this.comparingVersions) {
          this.loadMainVersion();
        }
      }
    );

    this.eventComparingVersion =
      this.approvalService.changeComparingVersion.subscribe(() => {
        if (this.comparingVersions) {
          this.loadComparingVersion();
        }
      });

    // Escutar mudanças de versão no modo "visualizar todas"
    this.eventVersion = this.approvalService.changeVersion.subscribe(() => {
      if (!this.comparingVersions) {
        // Recarregar dados quando a versão mudar (modo visualizar todas)
        this.getApprovalData();
      }
    });
  }

  ngAfterViewInit() {}

  ngOnDestroy() {
    this.eventMainVersion?.unsubscribe();
    this.eventComparingVersion?.unsubscribe();
    this.eventVersion?.unsubscribe();
  }

  loadComparingVersions() {
    // Só carregar se houver versões selecionadas
    if (this.approvalService.approvalData.mainVersion) {
      this.loadMainVersion();
    }
    if (this.approvalService.approvalData.comparingVersion) {
      this.loadComparingVersion();
    }
  }

  loadMainVersion() {
    if (!this.approvalService.approvalData.mainVersion) {
      this.mainData = [];
      this.mainApproval = null;
      this.mainRepresentatives = [];
      this.mainLoading = false;
      return;
    }
    // Limpar dados anteriores antes de carregar nova versão
    this.mainData = [];
    this.mainApproval = null;
    this.mainRepresentatives = [];
    this.mainLoading = true;
    this.getApprovalData('main');
  }

  loadComparingVersion() {
    if (!this.approvalService.approvalData.comparingVersion) {
      this.comparingData = [];
      this.comparingApproval = null;
      this.comparingRepresentatives = [];
      this.comparingDataLoading = false;
      this.comparingLoading = false;
      return;
    }
    // Limpar dados anteriores antes de carregar nova versão
    this.comparingData = [];
    this.comparingApproval = null;
    this.comparingRepresentatives = [];
    this.comparingDataLoading = true;
    this.comparingLoading = true;
    this.getApprovalData('comparing');
  }

  private canRunComparison(): boolean {
    return (
      this.comparingVersions &&
      !this.mainLoading &&
      !this.comparingDataLoading &&
      !!this.mainData &&
      !!this.comparingData
    );
  }

  // Função para comparar os dados e aplicar classes de comparação
  // Compara APENAS por ID dos arquivos
  async compareData() {
    try {
      if (!this.canRunComparison()) {
        return;
      }

      // Se ambas as versões estão vazias, não há nada para comparar
      if (
        (!this.mainData || this.mainData.length === 0) &&
        (!this.comparingData || this.comparingData.length === 0)
      ) {
        this.comparingData = [];
        this.cdr.detectChanges();
        return;
      }

      // Coletar todos os IDs de arquivos da versão principal
      const mainFileIds = new Set<number>();
      (this.mainData || []).forEach((item: any) => {
        (item.files || []).forEach((file: any) => {
          if (file.id) {
            mainFileIds.add(file.id);
          }
        });
      });

      // Se main está vazio, todos os itens de comparing são novos
      if (!this.mainData || this.mainData.length === 0) {
        const processedData = (this.comparingData || []).map((item: any) => ({
          ...item,
          compareClass: 'row-compare-added',
          changedFields: {},
        }));
        this.comparingData = processedData;
        this.cdr.detectChanges();
        return;
      }

      // Se comparing está vazio, todos os itens de main foram removidos
      if (!this.comparingData || this.comparingData.length === 0) {
        const processedData = (this.mainData || []).map((item: any) => ({
          ...item,
          compareClass: 'row-compare-removed',
          changedFields: {},
        }));
        this.comparingData = processedData;
        this.cdr.detectChanges();
        return;
      }

      // Coletar todos os IDs de arquivos da versão comparando (antes de processar)
      const comparingFileIds = new Set<number>();
      (this.comparingData || []).forEach((item: any) => {
        (item.files || []).forEach((file: any) => {
          if (file.id) {
            comparingFileIds.add(file.id);
          }
        });
      });

      const processedData: any[] = [];

      // Processar itens da tabela de comparação (direita)
      this.comparingData.forEach((comparingItem: any) => {
        // Verificar se algum arquivo deste item existe na versão principal (por ID)
        const comparingItemFileIds = (comparingItem.files || [])
          .map((f: any) => f.id)
          .filter((id: any) => id);
        const hasAnyExistingFile = comparingItemFileIds.some((id: number) =>
          mainFileIds.has(id)
        );
        const hasAnyNewFile = comparingItemFileIds.some(
          (id: number) => !mainFileIds.has(id)
        );

        // Se todos os arquivos existem na versão principal (mesmo ID), não é alteração
        if (hasAnyExistingFile && !hasAnyNewFile) {
          // Todos os arquivos existem - não marcar como alteração
          processedData.push({
            ...comparingItem,
            compareClass: '',
            changedFields: {},
          });
        } else if (hasAnyNewFile) {
          // Tem arquivos novos - marcar como adicionado
          processedData.push({
            ...comparingItem,
            compareClass: 'row-compare-added',
            changedFields: {},
          });
        } else {
          // Item não existe na versão principal - marcar como adicionado
          processedData.push({
            ...comparingItem,
            compareClass: 'row-compare-added',
            changedFields: {},
          });
        }
      });

      // Adicionar itens removidos (arquivos que existem só na esquerda)
      this.mainData.forEach((referenceItem: any) => {
        const referenceFileIds = (referenceItem.files || [])
          .map((f: any) => f.id)
          .filter((id: any) => id);
        const hasAnyRemovedFile = referenceFileIds.some(
          (id: number) => !comparingFileIds.has(id)
        );
        const hasAnyExistingFile = referenceFileIds.some((id: number) =>
          comparingFileIds.has(id)
        );

        // Se tem arquivos removidos, adicionar como removido
        if (hasAnyRemovedFile) {
          // Se todos os arquivos foram removidos, marcar todo o item como removido
          if (!hasAnyExistingFile) {
            processedData.push({
              ...referenceItem,
              compareClass: 'row-compare-removed',
              changedFields: {},
            });
          } else {
            // Alguns arquivos foram removidos mas outros ainda existem
            // Criar uma cópia do item marcando como removido
            processedData.push({
              ...referenceItem,
              compareClass: 'row-compare-removed',
              changedFields: {},
            });
          }
        }
      });

      this.comparingData = processedData;
      this.cdr.detectChanges();
    } catch (error) {
    }
  }

  async getApprovalData(versionType: 'main' | 'comparing' | null = null) {
    try {
      // Garantir que o loading esteja ativo quando começar a buscar dados
      if (!versionType) {
        this.loading = true;
        this.cdr.detectChanges();
      }

      const register_uuid = this.route.snapshot.params.id;

      if (!register_uuid) {
        throw new Error('Register UUID não encontrado na rota');
      }

      const params: any = {
        data_type: 'files',
      };

      // Determinar qual versão buscar
      if (
        versionType === 'main' &&
        this.approvalService.approvalData.mainVersion
      ) {
        params.version = this.approvalService.approvalData.mainVersion;
      } else if (
        versionType === 'comparing' &&
        this.approvalService.approvalData.comparingVersion
      ) {
        params.version = this.approvalService.approvalData.comparingVersion;
      } else if (!versionType && this.approvalService.approvalData.version) {
        params.version = this.approvalService.approvalData.version;
      } else {
        params.last = true;
      }

      const response = await this.api.get<any>({
        route: `api/v3/registration/versions/register/${register_uuid}/`,
        params: params,
        token: true,
      });

      if (!response || !response.data || !response.data.versions?.files) {
        throw new Error('Resposta da API inválida');
      }

      // Verificar se há versões disponíveis
      const versions = response.data.versions.files.versions || [];

      // Se não há versões ou o array está vazio, limpar os dados
      if (versions.length === 0) {
        if (versionType === 'main') {
          this.mainData = [];
          this.mainApproval = null;
          this.mainRepresentatives = [];
          this.mainLoading = false;
          // Se já temos comparing data, fazer comparação mesmo com main vazio
          if (this.canRunComparison()) {
            await this.compareData();
          }
          this.cdr.detectChanges();
          return;
        } else if (versionType === 'comparing') {
          this.comparingData = [];
          this.comparingApproval = null;
          this.comparingRepresentatives = [];
          this.comparingLoading = false;
          this.comparingDataLoading = false;
          // Se já temos main data, fazer comparação mesmo com comparing vazio
          if (this.canRunComparison()) {
            await this.compareData();
          }
          this.cdr.detectChanges();
          return;
        } else {
          // Modo normal - limpar dados
          this.approval = null;
          this.representatives = [];
          this.data = [];
          this.loading = false;
          this.cdr.detectChanges();
          return;
        }
      }

      // Extrair dados da versão
      const versionData = versions[0];

      // Se não há dados da versão, tratar como vazio
      if (!versionData) {
        if (versionType === 'main') {
          this.mainData = [];
          this.mainApproval = null;
          this.mainRepresentatives = [];
          this.mainLoading = false;
          if (this.canRunComparison()) {
            await this.compareData();
          }
          this.cdr.detectChanges();
          return;
        } else if (versionType === 'comparing') {
          this.comparingData = [];
          this.comparingApproval = null;
          this.comparingRepresentatives = [];
          this.comparingLoading = false;
          this.comparingDataLoading = false;
          if (this.canRunComparison()) {
            await this.compareData();
          }
          this.cdr.detectChanges();
          return;
        } else {
          this.approval = null;
          this.representatives = [];
          this.data = [];
          this.loading = false;
          this.cdr.detectChanges();
          return;
        }
      }

      const filesData = versionData?.data || [];

      // Usar representantes já carregados do service (não fazer nova chamada)
      let representativesData: any[] = [];

      // Tentar obter do cache do service
      if (this.approvalService.approvalData.representative) {
        representativesData = this.approvalService.approvalData.representative;
      } else if (this.approvalService.approvalData.attachmentsRepresentatives) {
        representativesData =
          this.approvalService.approvalData.attachmentsRepresentatives;
      }

      // Mapear para o formato esperado (simular approval.register.files)
      const mappedApproval = {
        register: {
          files: filesData.map((file: any) => ({
            id: file.id,
            file: file.file,
            type: file.type?.name || file.type || '', // type como string (nome)
            situation: file.situation,
            situation_display: file.situation_display,
            description: file.description,
            date_created: file.date_created,
            date_updated: file.date_updated,
            date_due: file.date_due,
            end_date_due: file.end_date_due,
            referent: file.referent,
            referent_id: file.referent_id,
            referent_display: file.referent_display,
            representative: file.representative, // Manter representative original
            type_obj: file.type, // Manter objeto completo para uso futuro
            deleted: file.deleted,
            fund: file.fund,
            user: file.user,
          })),
          representatives: representativesData,
        },
      };

      if (versionType === 'main') {
        this.mainApproval = mappedApproval;
        this.mainRepresentatives = representativesData;
        const tempApproval = { ...this.mainApproval };
        this.approval = tempApproval;
        this.representatives = representativesData;
        this.filterData();
        this.mainData = [...this.data];
        this.mainLoading = false;
        // Se já temos comparing data, fazer comparação
        if (this.canRunComparison()) {
          await this.compareData();
        }
        this.cdr.detectChanges();
      } else if (versionType === 'comparing') {
        this.comparingApproval = mappedApproval;
        this.comparingRepresentatives = representativesData;
        const tempApproval = { ...this.comparingApproval };
        this.approval = tempApproval;
        this.representatives = representativesData;
        this.filterData();
        this.comparingData = [...this.data];
        this.comparingLoading = false;
        this.comparingDataLoading = false;
        // Se já temos main data, fazer comparação
        if (this.canRunComparison()) {
          await this.compareData();
        }
        this.cdr.detectChanges();
      } else {
        // Armazenar no cache do service
        this.approvalService.approvalData.attachmentsData = mappedApproval;
        this.approvalService.approvalData.attachmentsRepresentatives =
          representativesData;

        this.approval = mappedApproval;
        this.representatives = representativesData || [];

        // Processar e filtrar os dados
        this.filterData();

        // Garantir que tudo esteja processado antes de desativar loading
        // Aguardar um ciclo de detecção de mudanças para garantir que tudo foi processado
        this.cdr.detectChanges();
        // Usar requestAnimationFrame para garantir que a view foi atualizada
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.loading = false;
            this.cdr.detectChanges();
          });
        });
      }
    } catch (error) {
      console.error('Erro ao buscar dados de anexos:', error);
      if (versionType === 'main') {
        this.mainLoading = false;
        this.mainData = [];
      } else if (versionType === 'comparing') {
        this.comparingLoading = false;
        this.comparingDataLoading = false;
        this.comparingData = [];
      } else {
        this.loading = false;
        this.data = [];
      }
      this.cdr.detectChanges();
    }
  }

  filterData() {
    if (
      !this.approval ||
      !this.approval.register ||
      !this.approval.register.files
    ) {
      this.data = [];
      return;
    }

    const attachmentTypes: AttachmentArrProps[] = [];
    const newData = [];

    this.approval.register.files.forEach((file: any) => {
      const label = file.type || '';
      const referent_id = file.referent_id ?? 'register';

      const index = attachmentTypes.findIndex(
        (item) => item.referent === referent_id
      );

      if (index === -1) {
        attachmentTypes.push({
          labelArr: [label],
          referent: referent_id,
        });
      } else {
        const alreadyHaveLabel = attachmentTypes[index]?.labelArr
          ? attachmentTypes[index].labelArr.find((item) => item === label)
          : false;
        if (!alreadyHaveLabel) {
          attachmentTypes[index]?.labelArr.push(label);
        }
      }
    });

    attachmentTypes.forEach((item: AttachmentArrProps) => {
      const files = item.labelArr.map((label) => {
        const file = this.approval.register.files.filter((file) => {
          return item.referent !== 'register'
            ? file.type === label && file.referent_id === item.referent
            : file.type === label && file.referent === item.referent;
        });

        return file;
      });

      files.forEach((file, idx) => {
        const label = item.labelArr[idx];

        newData.push({
          label,
          files: file,
        });
      });

      this.data = newData;
    });

    // Ordenar para que documentos do titular apareçam primeiro
    this.data.sort((a, b) => {
      const aIsTitular =
        a.files[0]?.referent === 'register' ||
        a.files[0]?.referent_id === 'register';
      const bIsTitular =
        b.files[0]?.referent === 'register' ||
        b.files[0]?.referent_id === 'register';

      if (aIsTitular && !bIsTitular) return -1;
      if (!aIsTitular && bIsTitular) return 1;
      return 0;
    });
  }

  getTitularName(): string {
    // Tentar obter do approval principal através do service
    const mainApproval = this.approvalService.approvalData.approval;

    if (mainApproval && mainApproval.register) {
      if (
        mainApproval.register.type?.toUpperCase() === 'PF' &&
        mainApproval.register.person
      ) {
        return mainApproval.register.person.full_name || '';
      }

      if (
        mainApproval.register.type?.toUpperCase() === 'PJ' &&
        mainApproval.register.company
      ) {
        return mainApproval.register.company.corporate_name || '';
      }
    }

    // Fallback: tentar obter do approval atual
    if (this.approval && this.approval.register) {
      if (
        this.approval.register.type?.toUpperCase() === 'PF' &&
        this.approval.register.person
      ) {
        return this.approval.register.person.full_name || '';
      }

      if (
        this.approval.register.type?.toUpperCase() === 'PJ' &&
        this.approval.register.company
      ) {
        return this.approval.register.company.corporate_name || '';
      }
    }

    return '';
  }

  getTitularRole(): string {
    // Tentar obter do approval principal através do service
    const mainApproval = this.approvalService.approvalData.approval;

    if (mainApproval && mainApproval.register && mainApproval.register.role) {
      return mainApproval.register.role.applicable_display || '';
    }

    // Fallback: tentar obter do approval atual
    if (
      this.approval &&
      this.approval.register &&
      this.approval.register.role
    ) {
      return this.approval.register.role.applicable_display || '';
    }

    return '';
  }

  getTitularDocument(): string {
    // Tentar obter do approval principal através do service
    const mainApproval = this.approvalService.approvalData.approval;

    if (mainApproval && mainApproval.register) {
      if (
        mainApproval.register.type?.toUpperCase() === 'PF' &&
        mainApproval.register.person?.document?.number
      ) {
        const cpf = mainApproval.register.person.document.number.replace(
          /\D/g,
          ''
        );
        if (cpf.length === 11) {
          return `${cpf.substring(0, 3)}.${cpf.substring(3, 6)}.${cpf.substring(
            6,
            9
          )}-${cpf.substring(9)}`;
        }
        return mainApproval.register.person.document.number;
      }

      if (
        mainApproval.register.type?.toUpperCase() === 'PJ' &&
        mainApproval.register.company?.document?.number
      ) {
        const cnpj = mainApproval.register.company.document.number.replace(
          /\D/g,
          ''
        );
        if (cnpj.length === 14) {
          return `${cnpj.substring(0, 2)}.${cnpj.substring(
            2,
            5
          )}.${cnpj.substring(5, 8)}/${cnpj.substring(8, 12)}-${cnpj.substring(
            12
          )}`;
        }
        return mainApproval.register.company.document.number;
      }
    }

    // Fallback: tentar obter do approval atual
    if (this.approval && this.approval.register) {
      if (
        this.approval.register.type?.toUpperCase() === 'PF' &&
        this.approval.register.person?.document?.number
      ) {
        const cpf = this.approval.register.person.document.number.replace(
          /\D/g,
          ''
        );
        if (cpf.length === 11) {
          return `${cpf.substring(0, 3)}.${cpf.substring(3, 6)}.${cpf.substring(
            6,
            9
          )}-${cpf.substring(9)}`;
        }
        return this.approval.register.person.document.number;
      }

      if (
        this.approval.register.type?.toUpperCase() === 'PJ' &&
        this.approval.register.company?.document?.number
      ) {
        const cnpj = this.approval.register.company.document.number.replace(
          /\D/g,
          ''
        );
        if (cnpj.length === 14) {
          return `${cnpj.substring(0, 2)}.${cnpj.substring(
            2,
            5
          )}.${cnpj.substring(5, 8)}/${cnpj.substring(8, 12)}-${cnpj.substring(
            12
          )}`;
        }
        return this.approval.register.company.document.number;
      }
    }

    return '';
  }

  formatPartnerName(
    partner: any,
    versionType: 'main' | 'comparing' | null = null
  ) {
    if (!partner) return '';

    // Se for documento do titular, retornar o nome do titular
    if (partner.referent === 'register' || partner.referent_id === 'register') {
      return this.getTitularName();
    }

    const representativesToUse = this.getRepresentativesForView(versionType);

    if (partner.referent === 'representative') {
      // Se o representative não vier preenchido, buscar na lista de representantes
      if (!partner.representative && partner.referent_id) {
        // Tentar encontrar pelo representative_register.id
        const representative = representativesToUse.find((rep) => {
          // Verificar se tem representative_register com id
          if (rep.representative_register?.id === partner.referent_id) {
            return true;
          }
          // Verificar se tem representative_register_id direto
          if (rep.representative_register_id === partner.referent_id) {
            return true;
          }
          // Comparar como string
          if (
            String(rep.representative_register_id) ===
            String(partner.referent_id)
          ) {
            return true;
          }
          // Verificar se o id do register do representante corresponde
          if (
            rep.representative_register &&
            String(rep.representative_register.id) ===
              String(partner.referent_id)
          ) {
            return true;
          }
          return false;
        });

        if (representative) {
          return representative.full_name || 'Representante';
        }
      } else if (partner.representative) {
        // Se o representative vier preenchido, usar diretamente
        return partner.representative.full_name || 'Representante';
      } else {
        // Tentar buscar mesmo sem representative null explícito
        if (partner.referent_id) {
          const representative = representativesToUse.find((rep) => {
            if (rep.representative_register?.id === partner.referent_id) {
              return true;
            }
            if (rep.representative_register_id === partner.referent_id) {
              return true;
            }
            if (
              String(rep.representative_register_id) ===
              String(partner.referent_id)
            ) {
              return true;
            }
            return false;
          });

          if (representative) {
            return representative.full_name || 'Representante';
          }
        }
      }

      return 'Representante';
    }

    if (partner.register && partner.register.company) {
      return partner.register.company.corporate_name;
    }

    if (partner.register && partner.register.person) {
      return partner.register.company.full_name;
    }

    return '-';
  }

  getRepresentativeCpf(
    partner: any,
    versionType: 'main' | 'comparing' | null = null
  ): string {
    if (!partner || partner.referent !== 'representative') {
      return '';
    }

    // Se o partner.representative vier preenchido, tentar pegar o CPF dele primeiro
    if (partner.representative) {
      let documentNumber =
        partner.representative.document_number ||
        partner.representative.document?.number;

      if (documentNumber) {
        // Formatar CPF se necessário (adicionar pontos e traço)
        const cpf = documentNumber.replace(/\D/g, '');
        if (cpf.length === 11) {
          return `${cpf.substring(0, 3)}.${cpf.substring(3, 6)}.${cpf.substring(
            6,
            9
          )}-${cpf.substring(9)}`;
        }
        return documentNumber;
      }
    }

    const representativesToUse = this.getRepresentativesForView(versionType);

    if (!representativesToUse || representativesToUse.length === 0) {
      console.warn('⚠️ Nenhum representante disponível para buscar CPF');
      return '';
    }

    // Se o representative não vier preenchido, buscar na lista de representantes
    if (partner.referent_id) {
      const representative = representativesToUse.find((rep) => {
        // Verificar se tem representative_register com id
        if (rep.representative_register?.id === partner.referent_id) {
          return true;
        }
        // Verificar se tem representative_register_id direto
        if (rep.representative_register_id === partner.referent_id) {
          return true;
        }
        // Comparar como string
        if (
          String(rep.representative_register_id) === String(partner.referent_id)
        ) {
          return true;
        }
        // Verificar se o id do register do representante corresponde
        if (
          rep.representative_register &&
          String(rep.representative_register.id) === String(partner.referent_id)
        ) {
          return true;
        }
        return false;
      });

      if (representative) {
        // Tentar obter document_number de diferentes lugares
        let documentNumber = representative.document_number;

        // Se não tiver document_number direto, tentar pegar do representative_register
        if (!documentNumber && representative.representative_register) {
          if (representative.representative_register.person?.document?.number) {
            documentNumber =
              representative.representative_register.person.document.number;
          } else if (
            representative.representative_register.company?.document?.number
          ) {
            documentNumber =
              representative.representative_register.company.document.number;
          }
        }

        if (documentNumber) {
          // Formatar CPF se necessário (adicionar pontos e traço)
          const cpf = documentNumber.replace(/\D/g, '');
          if (cpf.length === 11) {
            return `${cpf.substring(0, 3)}.${cpf.substring(
              3,
              6
            )}.${cpf.substring(6, 9)}-${cpf.substring(9)}`;
          }
          return documentNumber;
        } else {
          console.warn(
            '⚠️ Representante encontrado mas sem document_number:',
            representative
          );
        }
      } else {
        console.warn(
          '⚠️ Representante NÃO encontrado para referent_id:',
          partner.referent_id,
          'Total de representantes:',
          representativesToUse.length
        );
      }
    }

    return '';
  }

  getRepresentativeSubtitle(
    partner: any,
    versionType: 'main' | 'comparing' | null = null
  ): string {
    if (!partner || partner.referent !== 'representative') {
      return '';
    }

    const cpf = this.getRepresentativeCpf(partner, versionType);
    if (cpf) {
      return `${cpf} • Representante`;
    }

    return 'Representante';
  }

  formatAttachmentType(referent) {
    if (referent === 'partner') {
      return 'Acionista';
    }

    if (referent === 'representative') {
      return 'Representante';
    }

    return 'Documentos do titular';
  }

  openFile(attachments: Partial<AttachmentListValues>[]) {
    if (!attachments || attachments.length === 0) {
      return;
    }

    this.modalFiles = attachments;

    const newModalFilesUrls = attachments.map(
      (attachment: Partial<AttachmentListValues>, index: number) => {
        const imgUrl = this.getImgUrl(attachment);
        return imgUrl;
      }
    );

    this.modalFilesUrls = newModalFilesUrls;
    this.selectedFile = newModalFilesUrls[0];

    if (!this.modalRoot) {
      return;
    }

    try {
      this.modalRoot.show();
    } catch (error) {
      console.error('❌ Erro ao abrir modal:', error);
    }

    this.handlerModalClose();
  }

  handlerModalClose() {
    const modalClose = document.querySelector('.dt-icon-close');

    modalClose.addEventListener('click', () => {
      this.selectedFile = null;
    });
  }

  setPrevAttachment() {
    const index = this.modalFilesUrls?.findIndex(
      (item) => item.url === this.selectedFile?.url
    );

    if (index === 0) return;

    this.selectedFile = this.modalFilesUrls[index - 1];
  }

  setNextAttachment() {
    const index = this.modalFilesUrls?.findIndex(
      (item) => item.url === this.selectedFile?.url
    );

    if (index === this.modalFilesUrls?.length - 1) return;

    this.selectedFile = this.modalFilesUrls[index + 1];
  }

  getDisabled(direction) {
    const index = this.modalFilesUrls?.findIndex(
      (item) => item.url === this.selectedFile?.url
    );

    if (direction === 'prev') {
      return index === 0;
    } else {
      return index === this.modalFilesUrls?.length - 1;
    }
  }

  renderIcon(file: any) {
    if (!file) return '';

    // Se file é um objeto com url.file, usar url.file
    // Se file é um objeto com file, usar file
    // Se file é uma string, usar diretamente
    let fileUrl = '';
    if (typeof file === 'object') {
      if (file.url && file.url.file) {
        fileUrl = file.url.file;
      } else if (file.file) {
        fileUrl = file.file;
      } else {
        return '';
      }
    } else if (typeof file === 'string') {
      fileUrl = file;
    } else {
      return '';
    }

    const fileExtension = fileUrl.split('.').pop()?.toLowerCase();

    let extension = '';

    switch (fileExtension) {
      case 'pdf':
        extension = 'icon_filepdf';
        break;
      case 'jpg':
      case 'jpeg':
        extension = 'icon_filejpg';
        break;
      case 'png':
        extension = 'icon_filepng';
        break;
      default:
        extension = 'icon_filejpg';
        break;
    }

    return `/assets/images/${extension}.svg`;
  }

  formatFileName(file: string) {
    if (!file) {
      return 'arquivo';
    }
    const fileName = file.split('/').pop()?.split('.').shift();
    return fileName || 'arquivo';
  }

  getAttachmentDate(term: string, file: string | number) {
    const date = new Date(file);
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();

    if (term === 'start') {
      return `${day}/${month}/${year}`;
    } else {
      return `${day}/${month}/${year + 2}`;
    }
  }

  formatDate(date: string) {
    const update = new Date(date);
    const today = new Date();

    const contDays = Math.floor(
      (today.getTime() - update.getTime()) / (1000 * 3600 * 24)
    );

    if (contDays === 0) {
      return 'Hoje';
    }

    if (contDays === 1) {
      return 'Ontem';
    }

    if (contDays > 1) {
      return `há ${contDays + 1} dias atrás`;
    }

    return '';
  }

  redirectTo(path: string) {
    this.router.navigateByUrl(path);
  }

  closeModal() {
    this.warningModal = false;
    this.reproveModal = false;
    this.modalFiles = null;

    this.reproveForm.reset();
  }

  openReproveModal() {
    this.closeModal();
    this.reproveModal = true;
  }

  openWarningModal(item: Partial<AttachmentListValues>) {
    this.warningModal = true;
    this.selectedAttachment = item;
  }

  async handleReproveDocument() {
    try {
      const res = await this.api.put({
        route: 'api/approvals/approval_file',
        token: true,
        body: {
          id: this.selectedAttachment?.id,
          situation: 2,
          description: this.reproveForm.controls.description.value,
        },
      });

      this.reproveModal = false;
      this.onClick.emit();

      this.toastService.show(
        'info',
        'Documento reprovado',
        'Sua solicitação foi enviada ao responsável'
      );
    } catch (error) {
      console.error(error);
    }
  }

  getImgUrl(attachment: Partial<AttachmentListValues>) {
    // Verificar diferentes estruturas de dados
    let fileUrl: string = '';

    if (attachment.url && attachment.url.file) {
      // Estrutura padrão: attachment.url.file
      fileUrl = attachment.url.file;
    } else if (attachment.file) {
      // Estrutura alternativa: attachment.file (modo comparação)
      fileUrl = attachment.file;
    } else if (typeof attachment === 'string') {
      // Se attachment é uma string direta
      fileUrl = attachment;
    } else {
      return {
        url: '',
        isImg: false,
        name: 'Arquivo não encontrado',
      };
    }

    if (!fileUrl) {
      return {
        url: '',
        isImg: false,
        name: 'URL não disponível',
      };
    }

    // Concatenar com a URL base da API se o caminho não começar com http:// ou https://
    let fullUrl = fileUrl;
    if (!fileUrl.startsWith('http://') && !fileUrl.startsWith('https://')) {
      fullUrl = this.api.url + fileUrl;
    }

    let url: any = fullUrl;
    let type: string = '';

    const regex = /\.([0-9a-z]+)(?:[\?#]|$)/i;
    const resultado = regex.exec(fileUrl);

    if (resultado && resultado[1]) {
      type = resultado[1].toLowerCase();
    }

    if (type === 'pdf') {
      url = this.sanitizer.bypassSecurityTrustResourceUrl(fullUrl);
    }

    if (type === 'xls' || type === 'xlsx') {
      url = this.sanitizer.bypassSecurityTrustResourceUrl(
        `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
          fullUrl
        )}`
      );
    }

    let auxExtensionTypes = ['pdf', 'xls', 'xlsx'];
    const isImg = !auxExtensionTypes.includes(type);

    const result = {
      url,
      isImg,
      name: this.formatFileName(fileUrl),
    };

    return result;
  }

  setHistoryData() {
    this.filterData();
    this.representatives =
      this.approval.history.Representative ||
      this.approval.register.representatives;
  }

  urlDecoding(label: string) {
    return decodeURIComponent(label);
  }

  getRepresentativesForView(
    versionType: 'main' | 'comparing' | null = null
  ): any[] {
    // Priorizar dados do cache do service (representantes já carregados)
    if (this.approvalService.approvalData.representative) {
      return this.approvalService.approvalData.representative;
    }

    if (!this.comparingVersions) return this.representatives;
    if (versionType === 'main') return this.mainRepresentatives;
    if (versionType === 'comparing') return this.comparingRepresentatives;
    return this.representatives;
  }
}
